'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Streamdown } from 'streamdown';
import { cn } from '@/lib/utils';
import { Calendar, Clock, ChevronLeft, ChevronRight, Share2, Linkedin, Twitter, Facebook, Link as LinkIcon, Heart, Plus, Copy, Check } from 'lucide-react';
import PreloadResources from '../components/PreloadResources';
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { SocialButton } from "@/components/base/buttons/social-button";
import { Input } from "@/components/base/input/input";
import { api } from '../services/api';
import SocialShare from '../components/SocialShare';
import prof1 from '../assets/reviews/prof_1.webp';


import BlogBreadcrumbs from '@/components/BlogBreadcrumbs';

import { BlurImage } from '@/components/base/images/BlurImage';

const BlogDetails = ({ article, relatedArticles = [] }) => {
    const [activeSection, setActiveSection] = useState('');
    const [headings, setHeadings] = useState([]);
    const [copied, setCopied] = useState(false);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(typeof window !== 'undefined' ? window.location.href : '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Newsletter State
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterStatus, setNewsletterStatus] = useState('idle');

    const handleNewsletterSubscribe = async (e) => {
        e.preventDefault();
        if (!newsletterEmail) return;
        setNewsletterStatus('loading');
        try {
            await api.joinWaitlist({ email: newsletterEmail, type: 'newsletter', pageSource: 'blog_details_footer' });
            setNewsletterStatus('success');
            setNewsletterEmail('');
        } catch (error) {
            console.error(error);
            setNewsletterStatus('error');
        }
    };

    const contentRef = React.useRef(null);
    const observerRef = React.useRef(null);

    // Scroll spy for Table of Contents
    useEffect(() => {
        if (!contentRef.current) return;

        const elements = contentRef.current.querySelectorAll('h2, h3');
        const headingData = [];
        const idMap = new Map();

        // Generate IDs and collect headings
        elements.forEach((el, index) => {
            let id = el.id;

            if (!id) {
                const slug = el.innerText
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)/g, '');
                id = slug || `heading-${index}`;
            }

            // Ensure unique IDs
            if (idMap.has(id)) {
                let count = idMap.get(id);
                idMap.set(id, count + 1);
                id = `${id}-${count}`;
            } else {
                idMap.set(id, 1);
            }

            el.id = id;

            // Add scroll margin handling for sticky header
            el.classList.add('scroll-mt-32');

            headingData.push({
                id: el.id,
                text: el.innerText,
                level: Number(el.tagName.substring(1))
            });
        });

        setHeadings(headingData);

        // Disconnect previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // New Intersection Observer
        const callback = (entries) => {
            const visibleEntries = entries.filter(e => e.isIntersecting);
            if (visibleEntries.length > 0) {
                const entry = visibleEntries[0];
                setActiveSection(entry.target.id);
            }
        };

        const observer = new IntersectionObserver(callback, {
            rootMargin: '-100px 0px -60% 0px',
            threshold: [0, 1]
        });

        observerRef.current = observer;
        elements.forEach((h) => observer.observe(h));

        return () => observer.disconnect();
    }, [article.content]);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="min-h-screen bg-[#FDFBF7] pt-24 pb-24">
            <div className="container container-prose">
                <div className="max-w-3xl mx-auto mb-8">
                    <BlogBreadcrumbs
                        items={[
                            { label: article.category, href: article.categorySlug ? `/blog/${article.categorySlug}` : null },
                            { label: article.title }
                        ]}
                        className="justify-center opacity-70 hover:opacity-100 transition-opacity"
                    />
                </div>

                <header className="max-w-3xl mx-auto text-center mb-12">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        {article.categorySlug ? (
                            <Link href={`/blog/${article.categorySlug}`}>
                                <Badge color="brand" size="md" className="hover:bg-brand-100 transition-colors cursor-pointer">
                                    {article.category}
                                </Badge>
                            </Link>
                        ) : (
                            <Badge color="brand" size="md">
                                {article.category}
                            </Badge>
                        )}
                        <span className="text-xs font-medium text-gray-500">{article.readTime || '5 min read'}</span>
                    </div>

                    <h1 className="text-display-md md:text-display-lg font-semibold text-gray-900 mb-6 leading-tight">
                        {article.title}
                    </h1>

                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                        {article.subtitle}
                    </p>

                    {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {article.tags.map((tag) => (
                                <Badge key={tag.id || tag} color="gray" size="sm" type="outline">
                                    {tag.name || tag}
                                </Badge>
                            ))}
                        </div>
                    )}
                </header>

                <div className="mb-16 rounded-2xl overflow-hidden shadow-sm border border-gray-100 aspect-video md:aspect-[21/9] relative bg-gray-100 group">
                    <BlurImage
                        src={article.image}
                        alt={article.title}
                        fill
                        priority
                        className="transition-transform duration-700 group-hover:scale-105"
                    />

                    <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />

                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <Avatar
                                src={article.author?.image}
                                name={article.author?.name}
                                size="lg"
                                className="h-12 w-12 border-2 border-white/20"
                                contrastBorder={false}
                            />
                            <div>
                                <h4 className="font-bold text-white text-lg leading-none mb-1">{article.author?.name}</h4>
                                <span className="text-sm text-gray-200">{article.date}</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2 text-sm font-medium text-white/90">
                                <Heart size={18} className="text-white fill-white/20" />
                                <span>{article.likes || 0}</span>
                            </div>

                            <div className="flex items-center gap-2 text-sm font-medium text-white/90">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
                                <span>{article.views || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="hidden lg:block lg:col-span-3 lg:col-start-1 relative">
                        <div className="sticky top-32">
                            {headings.length > 0 && (
                                <div className="mb-10">
                                    <h4 className="font-semibold text-gray-900 mb-4 text-xs uppercase tracking-wider">On this page</h4>
                                    <nav className="flex flex-col space-y-2 border-l-2 border-gray-100 dark:border-gray-800">
                                        {headings.map((heading) => (
                                            <button
                                                key={heading.id}
                                                onClick={() => scrollToSection(heading.id)}
                                                className={cn(
                                                    "text-left text-sm py-1 pl-4 transition-all duration-200 hover:text-brand-600 border-l-2 -ml-[2px]",
                                                    activeSection === heading.id
                                                        ? "text-brand-600 font-medium border-brand-600"
                                                        : "text-gray-500 border-transparent"
                                                )}
                                                style={{
                                                    paddingLeft: heading.level === 3 ? '1.5rem' : '1rem'
                                                }}
                                            >
                                                {heading.text}
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                            )}
                        </div>
                    </aside>

                    <article ref={contentRef} className="lg:col-span-8 lg:col-start-5 prose prose-lg prose-gray max-w-none prose-blockquote:not-italic prose-blockquote:font-medium prose-blockquote:text-gray-900 prose-blockquote:border-l-4 prose-blockquote:border-brand-900 prose-blockquote:bg-brand-50 prose-blockquote:py-4 prose-blockquote:pl-10 prose-blockquote:pr-6 prose-blockquote:rounded-r-xl prose-blockquote:mx-0">
                        <div dangerouslySetInnerHTML={{ __html: article.content }} />
                    </article>
                </div>

                <div className="mt-24">
                    <div className="bg-gray-50/80 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700 rounded-[2.5rem] overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-500/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                        <div className="p-8 md:p-12 relative z-10">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 mb-8 border-b border-gray-200 dark:border-gray-700/50">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">Share this article</h3>
                                    <p className="text-gray-500 text-sm">Spread the knowledge with your network.</p>
                                </div>
                                <SocialShare url={typeof window !== 'undefined' ? window.location.href : ''} title={article.title} />
                            </div>

                            <div className="flex flex-col md:flex-row gap-8 md:items-start">
                                {article.author?.image && (
                                    <div className="flex-shrink-0 relative">
                                        <Avatar
                                            src={article.author.image}
                                            alt={article.author.name}
                                            size="xl"
                                            className="h-24 w-24 md:h-32 md:w-32 border-4 border-white shadow-sm"
                                            contrastBorder={false}
                                        />
                                        <Badge size="sm" color="brand" className="absolute -bottom-3 left-1/2 -translate-x-1/2 shadow-sm whitespace-nowrap bg-brand-600 text-white border-none py-1 px-2.5">
                                            {article.author.postCount ? `${article.author.postCount} Posts` : 'Editor'}
                                        </Badge>
                                    </div>
                                )}

                                <div className="flex-1">
                                    <div className="mb-4">
                                        <h4 className="text-2xl font-bold text-gray-900 mb-1">{article.author.name}</h4>
                                        <p className="text-brand-600 font-medium">{article.author.role}</p>
                                    </div>

                                    {article.author.bio && (
                                        <div
                                            className="text-gray-600 dark:text-gray-300 leading-relaxed prose prose-sm max-w-none prose-p:my-2 prose-strong:font-semibold prose-strong:text-gray-800"
                                            dangerouslySetInnerHTML={{ __html: article.author.bio.replace(/\n/g, '<br />') }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {relatedArticles && relatedArticles.length > 0 && (
                    <div className="mt-24 pt-12">
                        <div className="flex items-center justify-between mb-10">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900">More articles for you</h3>
                            <div className="flex gap-3">
                                <button className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">
                                    <ChevronLeft size={20} />
                                </button>
                                <button className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {relatedArticles.map((item) => (
                                <Link key={item.id} href={`/blog/${item.id}`} className="group cursor-pointer flex flex-col h-full">
                                    <div className="rounded-2xl overflow-hidden mb-4 aspect-[4/3] relative border border-gray-100 dark:border-gray-800">
                                        <BlurImage
                                            src={item.image}
                                            alt={item.title}
                                            fill
                                            className="transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <Badge className="absolute top-3 left-3" size="sm" type="modern">
                                            {item.category}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                                        <span>{item.date}</span>
                                        <span>•</span>
                                        <span>{item.readTime || '5 min read'}</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 group-hover:text-brand-600 transition-colors line-clamp-2 leading-tight mb-2">
                                        {item.title}
                                    </h4>
                                    <p className="text-sm text-gray-500 line-clamp-2">{item.excerpt}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-24 bg-gray-900 rounded-[2.5rem] p-8 md:p-16 text-center text-white relative overflow-hidden isolate">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-600/30 blur-[120px] rounded-full -z-10 pointer-events-none opacity-50 mix-blend-screen" />

                    <div className="relative z-10 max-w-2xl mx-auto">
                        <Badge color="brand" className="mb-6 bg-brand-500/10 text-brand-300 border-brand-500/20">Newsletter</Badge>
                        <h2 className="text-display-sm md:text-display-md font-bold mb-4 tracking-tight">Subscribe to our newsletter</h2>
                        <p className="text-gray-400 mb-10 text-lg leading-relaxed">
                            Get the latest insights, career advice, and industry trends delivered directly to your inbox. No spam, just value.
                        </p>

                        {newsletterStatus === 'success' ? (
                            <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 transform animate-in fade-in zoom-in duration-300">
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-green-400">
                                    <Check size={32} />
                                </div>
                                <h3 className="text-xl font-semibold text-green-400 mb-2">You're subscribed!</h3>
                                <p className="text-green-500/80">Thank you for joining our community.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleNewsletterSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                                <Input
                                    type="email"
                                    placeholder="Enter your email"
                                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:bg-white/10 focus:border-brand-500 transition-all rounded-xl"
                                    value={newsletterEmail}
                                    onChange={(e) => setNewsletterEmail(e.target.value)}
                                    required
                                    disabled={newsletterStatus === 'loading'}
                                />
                                <Button
                                    type="submit"
                                    size="lg"
                                    color="primary"
                                    className="h-12 px-8 rounded-xl font-semibold"
                                    loading={newsletterStatus === 'loading'}
                                >
                                    Subscribe
                                </Button>
                            </form>
                        )}
                        {newsletterStatus === 'error' && (
                            <p className="mt-4 text-red-400 text-sm">Something went wrong. Please try again.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


export default BlogDetails;
